import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Badge } from '../components/ui/badge';
import { useBrandLabel } from '../lib/demoBrand';
import WavePulseGauge from '../components/metrics/WavePulseGauge';
import { WAVE_PULSE_BANDS } from '../components/metrics/waveMetrics';
import {
  HelpCircle, BookOpen, MessageCircle, Waves, Zap, Users, ChevronRight, ChevronDown, Play, FileText,
  Home, Search, Folder, ClipboardCheck, BarChart3, TrendingUp, Shield, Globe, Package,
  Eye, Activity, Sparkles,
} from 'lucide-react';

// ════════════════════════════════════════════════════════════════
// FEATURE GUIDES — what every page does, plain English
// Synced with the unified metric vocabulary in waveMetrics.js
// ════════════════════════════════════════════════════════════════

const FEATURE_GUIDES = [
  {
    id: 'home', title: 'Home Dashboard', icon: Home, path: '/home', color: '#00D4FF',
    summary: 'Your command center — live metrics, recent wave wins, quick actions.',
    details: [
      { label: 'Brand Pulse Ring', text: 'The big 0–100 gauge in your performance card is your Brand Pulse — your average Wave Pulse across every wave you\'ve ever run. Above 80 = top decile.' },
      { label: 'Performance at a Glance', text: 'Three mini-metrics: Wave Pulse (current), ROAS (return per dollar), Engagement Rate (% of viewers interacting). Hover the ⓘ icon for a definition any time.' },
      { label: 'Wave Orchestration Viz', text: 'When you have a live wave, the hero visual lights up with real-time creator activity and shows your current Wave Pulse score in the center. Otherwise it shows a Roster Preview of who\'s available.' },
      { label: 'Quick Actions', text: 'Plan New Wave, See Live Pulse, Unlock Your Footprint — all one click from the hero.' },
    ],
  },
  {
    id: 'build-wave', title: 'Build Wave', icon: Waves, path: '/build-wave', color: '#FF4D94',
    summary: 'Create and launch synchronized creator campaigns in 6 steps.',
    details: [
      { label: 'Step 1: Campaign Setup', text: 'Set your campaign title, goal, budget, timeline, and target platforms (TikTok, Instagram, YouTube).' },
      { label: 'Step 2: Geo-Targeting', text: 'Choose scope: National, State, DMA (Designated Market Area), or Zip Code precision.' },
      { label: 'Step 3: Creative Direction', text: 'Define deliverables, Smart KPIs, tone, and required hashtags. #ad is always included automatically.' },
      { label: 'Step 4: Creator Cohort', text: 'Pick from Digital DNA matches. Each creator card shows Burst Power (their first-hour engagement vs peer median) and Trend Fit (how well their audience overlaps with your other picks).' },
      { label: 'Step 5: Generate RFPs', text: 'AI writes personalized captions per creator. Every caption includes #ad. Review before sending.' },
      { label: 'Step 6: Send & Schedule', text: 'RFPs go to creators via the Wave App. Set activation time and sync window (default ±5 min).' },
    ],
  },
  {
    id: 'portfolio', title: 'Product Portfolio', icon: Package, path: '/portfolio', color: '#00D4FF',
    summary: 'Manage your SKUs, run R&D tests, and plan your wave calendar.',
    details: [
      { label: 'Health Score', text: 'Composite 0–100 reflecting your portfolio\'s wave-readiness based on SKU count, creator testing, and recent wave activity.' },
      { label: 'SKU Cards', text: 'Each product shows wave history, sentiment, retail velocity (sales pace), and quick actions: Launch Wave or Test in R&D Lab.' },
      { label: 'Wave Calendar', text: '12-month view of planned and past waves. Helps avoid overlap and identify whitespace.' },
      { label: 'R&D Lab', text: '4-step testing flow: Select SKU → Build Focus Group → Run Test (product sampling) → Analyze Results (purchase intent + sentiment).' },
    ],
  },
  {
    id: 'discover', title: 'Discover Creators', icon: Search, path: '/discover', color: '#FF4D94',
    summary: 'Advanced filter builder with 12 categories and global Creator Cart.',
    details: [
      { label: 'Quick Filter Pills', text: 'One-click presets: "Gen Z Women", "HBCU Creators", "Fitness Niche", "High Engagement"…' },
      { label: '12 Filter Categories', text: 'Demographics, Platform, Niche, Ethnicity & Culture, Location, Follower Tier, Engagement Rate, Cost Model, Content Style, Brand Safety, Availability, Past Performance.' },
      { label: 'Boolean Query Preview', text: 'See your query as you build it: (Gender: Female) AND (Platform: TikTok) AND (Niche: Fitness).' },
      { label: 'Add to Wave', text: 'Creators go to your global Creator Cart — visible as a floating banner on every page.' },
    ],
  },
  {
    id: 'campaigns', title: 'Campaigns', icon: Folder, path: '/campaigns', color: '#00D4FF',
    summary: 'All your waves, ranked by Wave Pulse. Click any to watch live.',
    details: [
      { label: 'Wave Cards', text: 'Each card shows status (LIVE/SCHEDULED/COMPLETED), timeline, platforms, creator count, budget, reach, ROAS, and Wave Pulse with a color-coded band.' },
      { label: 'Sort by Wave Pulse (default)', text: 'Live waves with the highest Pulse float to the top. Switch to ROAS, Budget, Reach, or Newest as needed.' },
      { label: 'Export Reports', text: 'PDF (full branded report) and PPTX (deck for leadership) on each row.' },
      { label: 'Summary Bar', text: 'Average wave cost, live waves count, total creators, average ROAS across all campaigns.' },
    ],
  },
  {
    id: 'campaign-detail', title: 'Campaign Detail & Live Wave Pulse', icon: Eye, path: '/campaigns', color: '#FF4D94',
    summary: 'Deep-dive into a single wave with 5 tabs and the live Wave Pulse hero.',
    details: [
      { label: 'Wave Pulse Hero', text: 'The big number at the top is your live Wave Pulse, 0–100. Color-coded by band (green = Forming, cyan = Compounding, yellow = Building, orange = Needs Boost, red = Stalled). Hover for the plain-English read.' },
      { label: 'Reach vs Views vs Impressions', text: 'Three different numbers, three different things. Reach = unique humans. Views = cumulative plays. Impressions = surface shows (incl. scroll-bys).' },
      { label: 'Platform Performance', text: 'Per-platform reach, engagement rate, ROAS, and top-performing content pieces.' },
      { label: 'In-Flight Controls', text: 'AI optimization recommendations with one-click Apply. Budget reallocation across platforms.' },
      { label: 'Creator Roster', text: 'Full roster table with avatar, name, platform, DNA Match %, Burst Power, status (Published/In Review/Scheduled), and performance rating.' },
    ],
  },
  {
    id: 'review-queue', title: 'Review Queue', icon: ClipboardCheck, path: '/drafts', color: '#00D4FF',
    summary: 'Approve or revise creator content before it auto-posts.',
    details: [
      { label: 'Content Items', text: 'Each submission shows creator name, platform, caption (with #ad highlighted), media preview, and timestamp.' },
      { label: 'Actions', text: 'Approve (clears for auto-post), Request Revision (sends feedback to creator), or AI Review (automated compliance scan).' },
      { label: 'Bulk Actions', text: '"Approve All" or "AI Review All" for batch processing.' },
      { label: 'FTC Compliance', text: 'AI reviewer checks every caption for #ad. Missing disclosure = high-severity flag. The platform never auto-posts without #ad.' },
    ],
  },
  {
    id: 'insights', title: 'Insights & Analytics', icon: BarChart3, path: '/insights', color: '#FF4D94',
    summary: 'Cross-wave charts, performance tables, and AI insights.',
    details: [
      { label: 'Summary Cards', text: 'Avg Wave Pulse, Reach · unique, Avg ROAS, Avg Engagement, Active Creators. Five numbers, no duplicates.' },
      { label: 'ROAS by Wave Chart', text: 'Horizontal bar chart ranking your waves by return on ad spend.' },
      { label: 'Reach by Wave Chart', text: 'Horizontal bar chart comparing total reach across waves.' },
      { label: 'Wave Performance Table', text: 'Wave name, Wave Pulse (with progress bar), reach, engagement rate, ROAS, sentiment, creator count. Click any row to drill in.' },
      { label: 'Smart Intelligence Tab', text: 'AI-generated strategic recommendations — e.g., "Creators with 20K–50K followers show 2.4× better ROAS than the 50K–100K tier".' },
    ],
  },
  {
    id: 'trending', title: "What's Trending", icon: TrendingUp, path: '/trending', color: '#00D4FF',
    summary: 'Viral challenges and trending creators ready for your next wave.',
    details: [
      { label: 'Trending Challenges', text: 'Cards with view count, participant count, platform, 24h growth rate, and an 8h engagement velocity sparkline.' },
      { label: 'Launch This Trend', text: 'One-click button that pre-fills Build Wave with the trend as your goal.' },
      { label: 'Ready Creators', text: 'Creators actively participating in trending content. "Add to Wave" drops them straight into your Cart.' },
    ],
  },
  {
    id: 'brand-safety', title: 'Brand Safety', icon: Shield, path: '/brand-safety', color: '#FF4D94',
    summary: 'Monitor FTC compliance, sentiment, and content safety across every wave.',
    details: [
      { label: 'Safety Dashboard', text: 'Total content, safe/warning/flagged/paused counts, and overall safety score.' },
      { label: 'Content Analysis', text: 'Every piece color-coded: Green (Safe), Yellow (Warning), Red (Flagged), Gray (Paused). Shows sentiment, compliance score, and specific issues.' },
      { label: 'Pause / Unpause', text: 'Stop content from publishing with a required reason. Restore once resolved.' },
      { label: 'Pre-Wave Predictor', text: 'Before launching, the safety system scans every queued caption for potential issues.' },
    ],
  },
  {
    id: 'data-room', title: '1st Party Data Room', icon: Globe, path: '/data-room', color: '#A78BFA',
    summary: 'Upload geographic data to visualize stores, customers, and wave performance on a live map.',
    details: [
      { label: '4 Data Uploads', text: 'Store Locations, Aggregated Customer Data, Hashed Customer List, Past Campaign Performance. Each has a downloadable template.' },
      { label: 'Interactive Map', text: 'Full US map with 4 toggleable layers: Store Pins, Customer Heatmap, Lookalike Overlap, Performance Overlay.' },
      { label: 'Insights Sidebar', text: 'Top states by customer density, Recommended Local Waves with Launch buttons, Predicted Sales Lift, Golden Local Creators.' },
      { label: 'Persistent Data', text: 'Uploads are stored server-side. Re-upload any layer to replace previous data.' },
    ],
  },
];

const QUICK_GUIDES = [
  { title: 'Reading Wave Pulse in 60 seconds',       desc: 'The single 0–100 number that tells you if your wave is forming a trend or stalling. What the colors mean and what to do next.', time: '3 min', icon: Activity, color: '#4ade80' },
  { title: 'Reach vs Views vs Impressions',          desc: 'Three different numbers, three different things. Why they look different and which one to share with your CMO.',                  time: '3 min', icon: Eye,      color: '#00D4FF' },
  { title: 'Building Your First Wave',               desc: 'Step-by-step: cohort selection, sync windows, RFP generation, launch.',                                                              time: '8 min', icon: Waves,    color: '#FF4D94' },
  { title: 'How Digital DNA Matching Works',         desc: 'How WAVE picks creators whose audiences actually overlap with yours — not just whoever has the right hashtag in their bio.',     time: '4 min', icon: Users,    color: '#00D4FF' },
  { title: 'Optimization Playbook',                  desc: 'In-flight wave optimization, cohort tuning, geo layering, budget reallocation. When and how to intervene.',                        time: '12 min', icon: FileText, color: '#FF4D94' },
  { title: 'RFP Flow & FTC Compliance',              desc: 'Send RFPs, review submissions, and ensure every caption includes #ad disclosure.',                                                  time: '6 min', icon: BookOpen, color: '#00D4FF' },
];

const FAQS = [
  { q: 'What is Wave Pulse and how do I read it?',
    a: 'Wave Pulse is the single 0–100 score that tells you whether your wave is forming a trend, building toward one, or stalled. The score combines four things WAVE already measures: post density (are creators firing on time?), cross-platform spread (is engagement crossing TikTok ↔ IG ↔ YT?), platform balance (no single platform dominating), and reply compression (are people replying to each other, not just the brand?). 85+ = forming a trend, 70–84 = compounding, 50–69 = building, 30–49 = needs boost, below 30 = stalled.' },
  { q: 'Reach, Views, Impressions — what\'s the difference?',
    a: 'Three different numbers. Reach = unique humans who saw your content (deduped across platforms). Views = cumulative plays (one person watching three posts = 3 views, 1 reach). Impressions = number of times your content appeared on a screen, including muted scroll-bys (always the largest of the three). The dashboard separates them so you don\'t accidentally quote impressions when your CMO is asking about reach.' },
  { q: 'What is Brand Pulse, and how is it different from Wave Pulse?',
    a: 'Wave Pulse is per-wave, live. Brand Pulse is the average of your Wave Pulse across every wave you\'ve ever run, percentile-ranked against other brands in your vertical. Brand Pulse is your track record. Wave Pulse is right now.' },
  { q: 'How does WAVE technology work?',
    a: 'WAVE coordinates content drops across thousands of nano- and micro-creators within a tight posting window (typically ±5 min). When dozens of authentic voices share a genuine experience at the same moment across overlapping audience pockets, audiences experience it as a real cultural moment, not staggered sponsored posts. The algorithm boost that comes from synchronized engagement is what compounds Wave Pulse into trend formation.' },
  { q: 'What is Digital DNA matching?',
    a: 'A creator-brand affinity score. We embed each creator into a 384-dimensional audience-signal vector based on who actually follows them, what they post about, and what gets engagement. Then we cosine-match those vectors against your brand\'s target audience centroid. Higher DNA Match = the creator\'s audience genuinely overlaps with yours, not just a self-declared niche.' },
  { q: 'What\'s the difference between Burst Power and Trend Fit on creator cards?',
    a: 'Burst Power = the creator\'s first-hour engagement rate vs their peer-tier median (0–100). Above 80 = patient-zero candidate. Trend Fit = how well their audience overlaps with the other creators already in your wave (0–100). High Burst Power + High Trend Fit = drop them in your cohort immediately.' },
  { q: 'How does FTC compliance work?',
    a: 'The platform enforces #ad at five layers: AI caption generation, template fallbacks, content review flagging, wave scheduling rejection, and auto-post injection. You never need to worry — the system handles it automatically.' },
  { q: 'Can I export campaign reports?',
    a: 'Yes. Every campaign has PDF and PPTX export buttons. Reports include the unified metric set (Wave Pulse + Reach + Views + Impressions + Engagement Rate + ROAS), top creators, micro-community breakdown, and AI recommendations.' },
  { q: 'What is the Creator Cart?',
    a: 'A global selection tool. When you click "Add to Wave" on any creator (from Discover, Trending, or Footprint), they\'re added to your cart. A floating banner at the bottom shows the count and a "Build Wave" button that carries them into the campaign builder.' },
  { q: 'What happens after I schedule a wave?',
    a: 'Creators are notified. At the activation time, the Wave Engine auto-posts all approved content within the sync window. Watch the Live Wave Dashboard for real-time events as posts go live, and the Wave Pulse hero will start showing a score as soon as enough creators have fired.' },
];

const GLOSSARY = [
  { term: 'Wave Pulse',                def: 'The single 0–100 score that tells you whether your wave is forming a trend, building, or stalled. Combines post density (40%), cross-platform spread (30%), platform balance (20%), and reply compression (10%).' },
  { term: 'Brand Pulse',               def: 'Lifetime average of Wave Pulse across every wave you\'ve ever run, percentile-ranked vs other brands in your vertical.' },
  { term: 'Reach',                     def: 'Unique humans who saw at least one post (deduped across platforms).' },
  { term: 'Views',                     def: 'Cumulative video plays. One human watching multiple posts = multiple views, one reach.' },
  { term: 'Impressions',               def: 'Times your content appeared on a screen, including muted autoplay and quick scroll-bys. Usually the biggest number.' },
  { term: 'Engagement Rate',           def: '(Likes + comments + shares) ÷ reach × 100. Industry baseline 1–3%, WAVE average 6.4%.' },
  { term: 'Engagement Velocity',       def: 'How fast engagement is accelerating, hour by hour. The shape of the curve — rising, flat, or falling.' },
  { term: 'ROAS',                      def: 'Return on Ad Spend — revenue per dollar invested in the wave.' },
  { term: 'Burst Power',               def: 'A creator\'s first-hour engagement rate versus their peer-tier median (0–100). Patient-zero candidate marker.' },
  { term: 'Trend Fit',                 def: 'How well a creator\'s audience overlaps with the other creators in your wave (0–100). Drives cross-platform spread.' },
  { term: 'Digital DNA Match',         def: 'Creator-brand affinity score from cosine similarity of audience-signal embeddings.' },
  { term: 'Wave',                      def: 'A synchronized campaign where all creators post within a tight temporal window.' },
  { term: 'Sync Window',               def: 'Time range (±5 min) within which all wave content publishes.' },
  { term: 'Cohort',                    def: 'The group of creators selected for a specific wave.' },
  { term: 'Patient Zero',              def: 'The first creator in a wave to fire — sets the engagement tone for everyone after.' },
  { term: 'Reacceleration',            def: 'Automatic in-flight intervention when Wave Pulse drops — fires a reply-storm burst or adds a fresh creator to revive momentum.' },
  { term: 'Velocity Band',             def: 'The cadence shape we pick for your wave (fast 24h sprint vs slow 7-day burn). Set during Build Wave.' },
  { term: 'Velocity Curve',            def: 'The predicted engagement curve over the wave\'s life. Live engagement is compared against it every minute.' },
  { term: 'RFP',                       def: 'Request for Proposal — the brand-approved brief sent to each creator.' },
  { term: 'Activation Time',           def: 'The exact moment a wave goes live (brand sets this).' },
  { term: 'Auto-Post',                 def: 'The platform posts content to creators\' social accounts automatically at the activation time.' },
  { term: 'FTC / #ad',                 def: 'Federal Trade Commission requires sponsored content to include #ad disclosure. Enforced at five layers.' },
];


// ════════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════════

export default function SupportPage() {
  const brandLabel = useBrandLabel();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [expandedGuide, setExpandedGuide] = useState(null);
  const [activeTab, setActiveTab] = useState('pulse');

  const tabs = [
    { id: 'pulse',      label: 'Reading Wave Pulse' },
    { id: 'guides',     label: 'Feature Guide' },
    { id: 'quickstart', label: 'Quick Start' },
    { id: 'faqs',       label: 'FAQs' },
    { id: 'glossary',   label: 'Metrics Glossary' },
  ];

  return (
    <DashboardLayout>
      <div className="p-5 lg:p-8 space-y-5 max-w-[1400px] mx-auto" data-testid="support-page">
        <div className="relative">
          <h1 className="text-2xl font-black text-white">Support & Glossary</h1>
          <p className="text-sm text-gray-500 mt-1">{brandLabel} &bull; Help Center, Wave Pulse Guide, and Metric Definitions</p>
          <span className="absolute top-0 right-0 text-[8px] text-gray-600 uppercase tracking-wider">Patent Pending</span>
        </div>

        {/* TAB NAVIGATION */}
        <div className="flex gap-1.5 border-b border-[#242730] pb-0 overflow-x-auto" data-testid="support-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2.5 text-xs font-semibold transition-all border-b-2 -mb-px whitespace-nowrap ${activeTab === t.id ? 'border-[#00D4FF] text-[#00D4FF]' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══ WAVE PULSE EXPLAINER TAB ═══ */}
        {activeTab === 'pulse' && <WavePulseExplainerTab />}

        {/* ═══ FEATURE GUIDE TAB ═══ */}
        {activeTab === 'guides' && (
          <div className="space-y-2" data-testid="feature-guides">
            <p className="text-xs text-gray-500 mb-3">Click any section to see detailed feature breakdowns. Click "Go to page" to navigate directly.</p>
            {FEATURE_GUIDES.map((g) => {
              const isOpen = expandedGuide === g.id;
              const Icon = g.icon;
              return (
                <div key={g.id} className="bg-[#242730] border border-[#242730] rounded-xl overflow-hidden" data-testid={`guide-section-${g.id}`}>
                  <button onClick={() => setExpandedGuide(isOpen ? null : g.id)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#242730]/70 transition-all">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${g.color}15` }}>
                      <Icon className="h-4 w-4" style={{ color: g.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-white">{g.title}</h4>
                        <Link to={g.path} onClick={e => e.stopPropagation()} className="text-[9px] px-2 py-0.5 rounded-full bg-[#181A20] text-gray-500 hover:text-[#00D4FF] transition-colors border border-[#2E3240]">
                          Go to page
                        </Link>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">{g.summary}</p>
                    </div>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-0 space-y-2 border-t border-[#2E3240] mt-0">
                      {g.details.map((d) => (
                        <div key={d.label} className="flex gap-3 py-2">
                          <div className="w-1 rounded-full flex-shrink-0" style={{ background: g.color, opacity: 0.4 }} />
                          <div>
                            <p className="text-[11px] font-bold text-white mb-0.5">{d.label}</p>
                            <p className="text-[11px] text-gray-400 leading-relaxed">{d.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ QUICK START TAB ═══ */}
        {activeTab === 'quickstart' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="quickstart-guides">
            {QUICK_GUIDES.map((g, i) => (
              <div key={g.title} className="bg-[#242730] border border-[#242730] rounded-xl p-4 hover:border-[#2E3240] transition-all group cursor-pointer" data-testid={`quickstart-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${g.color}15` }}>
                    <g.icon className="h-4 w-4" style={{ color: g.color }} />
                  </div>
                  <Badge className="bg-white/5 text-gray-400 border-white/10 text-[9px]">{g.time} read</Badge>
                </div>
                <h4 className="text-sm font-bold text-white group-hover:text-[#00D4FF] transition-colors mb-1">{g.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
        )}

        {/* ═══ FAQS TAB ═══ */}
        {activeTab === 'faqs' && (
          <div className="space-y-2" data-testid="faqs">
            {FAQS.map((faq, i) => (
              <div key={faq.q} className="bg-[#242730] border border-[#242730] rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left">
                  <span className="text-sm font-semibold text-white">{faq.q}</span>
                  <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${expandedFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {expandedFaq === i && (
                  <div className="px-4 pb-4 border-t border-[#2E3240]">
                    <p className="text-xs text-gray-400 leading-relaxed pt-3">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ═══ GLOSSARY TAB ═══ */}
        {activeTab === 'glossary' && (
          <div className="bg-[#242730] border border-[#242730] rounded-xl overflow-hidden" data-testid="glossary">
            <div className="divide-y divide-[#2E3240]">
              {GLOSSARY.map((g) => (
                <div key={g.term} className="flex flex-col md:flex-row gap-1 md:gap-4 px-4 py-3">
                  <span className="text-xs font-bold text-[#00D4FF] md:w-44 md:flex-shrink-0">{g.term}</span>
                  <span className="text-xs text-gray-400 leading-relaxed">{g.def}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LIVE CHAT */}
        <div className="bg-[#242730] border border-[#242730] rounded-xl p-5 text-center">
          <MessageCircle className="h-7 w-7 text-[#00D4FF] mx-auto mb-2" />
          <h3 className="text-base font-bold text-white mb-1">Need More Help?</h3>
          <p className="text-xs text-gray-500 mb-3">Our Wave specialists are standing by</p>
          <button className="btn-wave px-5 py-2.5 rounded-xl bg-[#FF4D94] text-white font-bold text-sm hover:brightness-110 transition-all"
                  style={{ boxShadow: '0 0 18px rgba(255,77,148,0.32)' }}
                  data-testid="live-chat-btn">
            <MessageCircle className="h-4 w-4 inline mr-2" />Start live chat
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}


// ════════════════════════════════════════════════════════════════
// Wave Pulse Explainer Tab — the single most important page
// ════════════════════════════════════════════════════════════════
function WavePulseExplainerTab() {
  const sampleScores = [92, 76, 58, 38, 18];
  return (
    <div className="space-y-5" data-testid="pulse-explainer-tab">
      {/* HEADLINE */}
      <div className="rounded-2xl p-6 lg:p-8 bg-gradient-to-br from-[#181A20] to-[#242730] border border-[#2E3240]">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          <WavePulseGauge score={87} size="lg" subtitle="Wave Pulse — the one number that tells you if your wave is working." />
          <div className="flex-1 space-y-3">
            <Badge className="bg-[#4ade80]/10 text-[#4ade80] border-[#4ade80]/20 text-[10px]">Plain English</Badge>
            <h3 className="text-xl lg:text-2xl font-black text-white">
              One number, 0–100. <span className="text-[#4ade80]">High = trend forming.</span> <span className="text-[#FF4D6B]">Low = stalled.</span>
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              Wave Pulse combines four signals — post density, cross-platform spread, platform balance, and reply compression —
              into a single live score. Watch it during the wave. If it stays above 70 for two hours, you have a hit on your hands.
              If it falls below 50, fire a reacceleration burst or pause to diagnose.
            </p>
          </div>
        </div>
      </div>

      {/* THE 5 BANDS */}
      <div className="rounded-2xl p-5 bg-[#242730] border border-[#2E3240]">
        <h3 className="text-sm font-black text-white mb-1 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#00D4FF]" /> The 5 Wave Pulse Bands
        </h3>
        <p className="text-[11px] text-gray-500 mb-4">Color-coded so you can read your dashboard in a glance.</p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {WAVE_PULSE_BANDS.map((b, i) => (
            <div key={b.label}
                 className="rounded-xl p-3 flex flex-col items-center text-center"
                 style={{ background: '#0f1219', border: `1px solid ${b.color}55` }}>
              <WavePulseGauge score={sampleScores[i]} size="sm" dense />
              <p className="text-[10px] font-black uppercase tracking-widest mt-2" style={{ color: b.color }}>{b.label}</p>
              <p className="text-[9px] text-gray-500 mt-1">{b.min}+ score</p>
              <p className="text-[10px] text-gray-300 leading-snug mt-2">{b.hint}</p>
            </div>
          ))}
        </div>
      </div>

      {/* THE FORMULA */}
      <div className="rounded-2xl p-5 bg-[#242730] border border-[#2E3240]">
        <h3 className="text-sm font-black text-white mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#FF4D94]" /> What goes into the score
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[
            { weight: '40%', name: 'Post density',          desc: 'Are creators firing on time? Auto-scheduler holds the burst window — brand doesn\'t manage talent timing, the platform does.', color: '#00D4FF' },
            { weight: '30%', name: 'Cross-engagement',      desc: 'Creator-to-creator reply chains. What TikTok flags as authentic conversation vs sponsored content.',                          color: '#FF4D94' },
            { weight: '20%', name: 'Multi-platform spread', desc: 'Wave reaching TikTok + IG + YT together. YouTube Shorts rewards this most; Reels rewards next.',                              color: '#A78BFA' },
            { weight: '10%', name: 'Comment depth',         desc: 'Replies to replies. The signal that turns a post from "viral" to "cultural moment" in the algorithm\'s eyes.',                 color: '#FFB020' },
          ].map(f => (
            <div key={f.name} className="rounded-lg p-3" style={{ background: '#0f1219', border: `1px solid ${f.color}33` }}>
              <p className="text-2xl font-black tabular-nums" style={{ color: f.color }}>{f.weight}</p>
              <p className="text-xs font-bold text-white mt-1">{f.name}</p>
              <p className="text-[10px] text-gray-400 leading-snug mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* iter 79 — WHY THESE PLATFORMS REWARD THIS */}
      <div className="rounded-2xl p-5 bg-[#242730] border border-[#2E3240]">
        <h3 className="text-sm font-black text-white mb-1 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#00D4FF]" /> Why platform algorithms reward Wave Pulse
        </h3>
        <p className="text-[11px] text-gray-500 mb-4">
          We didn&apos;t invent the formula &mdash; we mapped the signals every major platform&apos;s algorithm ranks highest.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#2E3240]">
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-gray-500 font-bold">Signal</th>
                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-gray-500 font-bold">TikTok</th>
                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-gray-500 font-bold">IG Reels</th>
                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-gray-500 font-bold">YT Shorts</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-gray-500 font-bold">Wave Pulse factor</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Early-hour engagement velocity', '⭐⭐⭐', '⭐⭐⭐', '⭐⭐⭐', 'Post density (40%)'],
                ['Cross-account reply chains',     '⭐⭐⭐', '⭐⭐',  '⭐⭐',  'Cross-engagement (30%)'],
                ['Multi-platform discovery',       '⭐⭐',  '⭐⭐',  '⭐⭐⭐', 'Multi-platform spread (20%)'],
                ['Comment depth',                  '⭐⭐⭐', '⭐⭐',  '⭐⭐',  'Comment depth (10%)'],
                ['Content completion rate',        '⭐⭐⭐', '⭐⭐⭐', '⭐⭐⭐', 'Per-creator Burst Power'],
              ].map(([signal, tt, ig, yt, factor]) => (
                <tr key={signal} className="border-b border-[#2E3240]/40 hover:bg-[#1a1d24]">
                  <td className="px-3 py-2 text-white">{signal}</td>
                  <td className="px-3 py-2 text-center text-[#FF4D94]">{tt}</td>
                  <td className="px-3 py-2 text-center text-[#A78BFA]">{ig}</td>
                  <td className="px-3 py-2 text-center text-[#FF4D6B]">{yt}</td>
                  <td className="px-3 py-2 text-[#00D4FF] font-bold">{factor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-gray-500 leading-snug mt-3 italic">
          Source: TikTok Creator Portal docs &middot; Instagram &ldquo;What&apos;s on Reels&rdquo; &middot; YouTube Shorts ranking blog posts (2024-2025). Wave Pulse weights are tuned to the union of all three.
        </p>
      </div>

      {/* AUTO-SCHEDULER EDUCATION */}
      <div className="rounded-2xl p-5 bg-[#181A20] border border-[#2E3240]">
        <h3 className="text-sm font-black text-white mb-2 flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#4ade80]" /> Why density is a guarantee, not a worry
        </h3>
        <p className="text-xs text-gray-300 leading-relaxed mb-2">
          Coordinating 150+ creators to post within a 5-minute window manually is impossible &mdash; talent miss windows, platforms throttle, time zones drift.
          That&apos;s the reliability problem WAVE solves. <span className="text-white font-bold">Auto-Scheduler</span> queues every accepted creator into the burst window the moment they approve their RFP.
          You don&apos;t manage talent timing. The platform does. That&apos;s how we land 100% density on enterprise waves of 200+ creators.
        </p>
        <p className="text-[10px] text-gray-500 leading-snug italic">
          When density drops below 90%, it&apos;s because creators rejected their RFP &mdash; bench creators are pre-warmed and the system mass-deploys them automatically.
        </p>
      </div>

      {/* WHAT TO DO */}
      <div className="rounded-2xl p-5 bg-[#181A20] border border-[#2E3240]">
        <h3 className="text-sm font-black text-white mb-3 flex items-center gap-2">
          <Zap className="h-4 w-4 text-[#4ade80]" /> What to do at each band
        </h3>
        <div className="space-y-2">
          {[
            { band: 'Forming a trend (85+)',  action: 'Hold course. Don\'t spend more — the wave is doing the work. Document the playbook, this is your win template.',      color: '#4ade80' },
            { band: 'Compounding (70–84)',   action: 'You\'re on track. Watch the engagement velocity curve — if it\'s still climbing, you\'ll cross 85 in the next 2 hrs.', color: '#00D4FF' },
            { band: 'Building (50–69)',      action: 'Add 5–8 fresh creators from a connected cluster. Engagement velocity slope is what you need to nudge upward.',          color: '#FFB020' },
            { band: 'Needs boost (30–49)',   action: 'Fire a reply-storm burst. Promote your top-performing post to your patient-zero creator\'s pinned slot.',                color: '#FF8800' },
            { band: 'Stalled (<30)',         action: 'Pause the wave. Audit the unique hook — it likely doesn\'t fit your cohort\'s natural posting tone. Re-customize.',     color: '#FF4D6B' },
          ].map(r => (
            <div key={r.band} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: `${r.color}0d`, borderLeft: `3px solid ${r.color}` }}>
              <span className="text-[10px] font-black uppercase tracking-wider w-44 flex-shrink-0" style={{ color: r.color }}>{r.band}</span>
              <span className="text-xs text-gray-300 leading-snug">{r.action}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
